/**
 * Auth Service — provider-based architecture.
 * Toggle via VITE_AUTH_PROVIDER=mock|real
 *   mock → local fixtures (see MockAuthProvider) for offline dev
 *   real → PATH.Api /api/v1/auth/* endpoints (first-party JWT auth)
 */

import { LO_BY_LOAN_STORAGE_KEY } from "@/lib/dbaTheme";
import { getStoredLoEmail, readCapturedLoEmail } from "@/lib/loEmail";
import { readLinkReferralForCreate } from "@/lib/linkReferral";
import type {
  User,
  AuthToken,
  RegisterRequest,
} from "@/types/auth";

// ---------- Registration errors ----------

/**
 * NP-447: "this email is already registered" needs to be distinguishable by the
 * UI, not just readable by a human, so the Create Account page can offer a
 * Sign In link instead of a generic Retry.
 *
 * Both providers previously flattened every registration failure into a plain
 * `Error` with a message string, so `Register.tsx` had no way to tell a
 * duplicate email from a network blip and could only render the generic banner.
 */
export class DuplicateEmailError extends Error {
  /** Checked instead of `instanceof` so detection survives module duplication. */
  readonly code = "duplicate_email" as const;
  constructor(message = "An account with this email already exists.") {
    super(message);
    this.name = "DuplicateEmailError";
  }
}

export function isDuplicateEmailError(err: unknown): err is DuplicateEmailError {
  return (
    err instanceof Error &&
    (err as { code?: unknown }).code === "duplicate_email"
  );
}

/**
 * Does a ProblemDetails `errors` map say the email is taken?
 *
 * PATH.Api returns this as **400 with the reason under `errors.Email`** — NOT a
 * 409 — so status alone cannot be the signal, and `errors.Email` alone is too
 * broad (it also carries "Email is not valid"). That leaves matching the
 * server's wording. Kept in one place so the coupling is visible and cheap to
 * replace if the API ever grows a machine-readable error code.
 */
const DUPLICATE_EMAIL_PATTERN =
  /already (exists|registered|in use)|already have an account|is taken|duplicate/i;

export function isDuplicateEmailProblem(
  fieldErrors: Record<string, string[]> | null | undefined,
): boolean {
  const messages = fieldErrors?.Email ?? fieldErrors?.email;
  return (
    Array.isArray(messages) &&
    messages.some(
      (m) => typeof m === "string" && DUPLICATE_EMAIL_PATTERN.test(m),
    )
  );
}

/**
 * NP-649: an invited co-borrower's address is taken but not usable — their row exists with
 * no password, so "Sign In" is a door /login answers 401 for. The remedy is the emailed
 * link, not the login page, so the Create Account form has to tell the two apart.
 *
 * Same coupling to server wording as `isDuplicateEmailProblem`, and checked BEFORE it
 * everywhere, because the two answers come back under the same `errors.Email` key and only
 * this one carries an action the borrower can complete.
 */
export class PendingActivationError extends Error {
  /** Checked instead of `instanceof` so detection survives module duplication. */
  readonly code = "pending_activation" as const;
  constructor(
    message = "Your account was created for you and still needs a password.",
  ) {
    super(message);
    this.name = "PendingActivationError";
  }
}

export function isPendingActivationError(
  err: unknown,
): err is PendingActivationError {
  return (
    err instanceof Error &&
    (err as { code?: unknown }).code === "pending_activation"
  );
}

const PENDING_ACTIVATION_PATTERN = /still needs a password/i;

export function isPendingActivationProblem(
  fieldErrors: Record<string, string[]> | null | undefined,
): boolean {
  const messages = fieldErrors?.Email ?? fieldErrors?.email;
  return (
    Array.isArray(messages) &&
    messages.some(
      (m) => typeof m === "string" && PENDING_ACTIVATION_PATTERN.test(m),
    )
  );
}

// ---------- Login request/response shaping ----------

/**
 * NP-549 AC-4 / LINK-423/424: `inviteToken` and `otc` are each additive and independent — a
 * login carrying neither sends exactly the body it sent before, and the two travel side by
 * side rather than one displacing the other, since they are different credentials from
 * different flows (a co-borrower's loan-access invite vs. a Lead-invited borrower's
 * loan-attach otc). Both go through verbatim: each is URL-safe base64 the API matches byte
 * for byte, so lower-casing or re-encoding either here would turn a live credential into an
 * invalid one.
 */
function loginBody(
  email: string,
  password: string,
  inviteToken?: string,
  otc?: string,
) {
  return {
    email,
    password,
    ...(inviteToken ? { inviteToken } : {}),
    ...(otc ? { otc } : {}),
  };
}

/**
 * NP-378: the captured `?lo=` address, as the optional `loanOfficerEmail` on a
 * registration body. It selects the DBA branch that brands the account-created
 * confirmation email (logo, colours, footer, NMLS, address); registration touches no
 * loan, so the API has no Vesta originator to infer the branch from and falls back to
 * corporate AFN branding when this is absent.
 *
 * Omitted rather than sent null or empty when there is no capture, which is what keeps
 * "no DBA link was followed" behaving exactly as registration did before.
 *
 * Read through `readCapturedLoEmail` — validating, unlike the raw `getStoredLoEmail`
 * that `resetPassword` uses — because `?lo=` is user-controllable and this endpoint 400s
 * on an invalid address (error key `LoanOfficerEmail`). Forwarding junk would turn a
 * cosmetic branding miss into a failed account creation.
 */
function capturedLoanOfficerField(): { loanOfficerEmail?: string } {
  const loanOfficerEmail = readCapturedLoEmail();
  return loanOfficerEmail ? { loanOfficerEmail } : {};
}

/**
 * NP-767 — the Link/Snap invite lead the borrower arrived through. The API refuses a registration
 * whose email is not that lead's, because an account under any other address never picks up the
 * invited loan. Omitted when there is no invite, which registers exactly as before.
 */
function invitedLeadField(): { leadId?: number } {
  const leadId = readLinkReferralForCreate()?.leadId;
  return leadId ? { leadId } : {};
}

/** Absent, null, or a non-string all mean the same thing: no loan was attached. */
function linkedLoanIdFrom(data: unknown): string | undefined {
  const value = (data as { linkedLoanId?: unknown } | null)?.linkedLoanId;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

/**
 * LINK-423/424 — the officer captured on a Lead-invite loan-attach otc, present only when
 * that otc actually attached a loan this request (mirrors `linkedLoanIdFrom`'s reasoning:
 * absent on every ordinary login, and absent whenever the otc presented did not redeem).
 */
function loanOfficerEmailFrom(data: unknown): string | undefined {
  const value = (data as { loanOfficerEmail?: unknown } | null)?.loanOfficerEmail;
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// ---------- Provider Interface ----------

/**
 * NP-549 AC-4 / LINK-423/424: `linkedLoanId` is the loan the API attached to this account
 * because the login carried a co-borrower invite token OR a Lead-invite loan-attach otc.
 * Absent on every other login, and absent whenever the credential presented had already
 * lapsed — the borrower in that state is still correctly signed in, so callers must read
 * "no loan attached", never "the login failed". The API does not say which of the two
 * credentials produced it; callers that need to distinguish them already know which one
 * they sent.
 *
 * `loanOfficerEmail` is LINK-423/424's own field — present only when the login's otc actually
 * attached a loan, so the caller can update `path_lo_email` to the invitation's own officer
 * once the attach has genuinely happened, not merely because an otc was presented.
 */
export interface LoginResult {
  user: User;
  token: AuthToken;
  linkedLoanId?: string;
  loanOfficerEmail?: string;
}

export interface AuthProvider {
  login(
    email: string,
    password: string,
    inviteToken?: string,
    otc?: string,
  ): Promise<LoginResult>;
  register(data: RegisterRequest): Promise<{ user: User; token: AuthToken }>;
  logout(): Promise<void>;
  resetPassword(email: string): Promise<void>;
  getToken(): Promise<string | null>;
  refreshToken(): Promise<AuthToken>;
  getCurrentUser(): Promise<User | null>;
  /**
   * NP-497 — merge fresh profile fields into the STORED user and return the result.
   *
   * The session user is written once at login and then never touched, so a profile
   * save left every consumer showing the old name until the borrower logged out and
   * back in. Returns null when there is no session to update.
   */
  updateStoredUser(patch: Partial<User>): Promise<User | null>;
  /** Persist the session across browser restarts (both providers implement this). */
  setPersist(persist: boolean): void;
}

// ---------- Mock Auth Provider ----------

const MOCK_USERS_KEY = "path_mock_users";
const MOCK_TOKEN_KEY = "path_access_token";
const MOCK_REFRESH_KEY = "path_refresh_token";
const MOCK_USER_KEY = "path_current_user";
const MOCK_PERSIST_KEY = "path_persist_session";
// NOTE: the NP-59 `path_auth_via_real_api` flag (wasAuthenticatedViaRealApi)
// was REMOVED with the e-consent mock-gating follow-up to #64: the consent
// API is consulted in EVERY auth mode, so no compliance gate distinguishes
// real-API sessions from mock ones anymore. `path_auth_via_real_api` is still
// swept from storage below so stale flags from older builds get cleaned up.
const LEGACY_AUTH_VIA_REAL_API_KEY = "path_auth_via_real_api";

export interface MockAuthSeedUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  password: string;
  phone?: string;
}

interface StoredUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  passwordHash: string;
  createdAt: string;
}

const DEFAULT_MOCK_USERS: StoredUser[] =
  import.meta.env.DEV || import.meta.env.VITE_AUTH_PROVIDER === "mock"
    ? [
        {
          id: "b0000001-0000-0000-0000-000000000001",
          email: "maria.santos@example.com",
          firstName: "Maria",
          lastName: "Santos",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        {
          // RCA-fix (2026-06-19): ID must match backend DevMockAuthMiddleware.MockUserMapping
          // which seeds james.chen with b0000002-...-000000000002. P0 FIX in middleware uses
          // JWT sub directly; mismatched GUID caused every loan ownership check to return 404.
          id: "b0000002-0000-0000-0000-000000000002",
          email: "james.chen@example.com",
          firstName: "James",
          lastName: "Chen",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        // Demo aliases — mapped by backend DevMockAuthMiddleware to real SQL Azure borrowers
        {
          id: "b0000001-0000-0000-0000-000000000001",
          email: "test@afncorp.com",
          firstName: "Maria",
          lastName: "Santos",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        {
          // RCA-fix (2026-06-19): demo@afncorp.com aliases james.chen → same GUID fix
          id: "b0000002-0000-0000-0000-000000000002",
          email: "demo@afncorp.com",
          firstName: "James",
          lastName: "Chen",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        // BUG-DEEP-004 fix: add test@path.com as a valid test credential
        {
          id: "b0000001-0000-0000-0000-000000000003",
          email: "test@path.com",
          firstName: "Path",
          lastName: "Tester",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        // Dedicated E2E test borrower with a unique GUID so no prior loans exist in dev DB.
        // Used by full-loan-creation-fixed.spec.ts to avoid redirect conflicts.
        {
          id: "b9e2e001-0000-0000-0000-e2e000000001",
          email: "e2e-borrower@afncorp.com",
          firstName: "E2E",
          lastName: "TestUser",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
        // Dedicated agent/automation test account — used by Tinker and CI curl verification.
        // Completely isolated GUID so tests never interfere with Maria/James loan data.
        {
          id: "btest001-0000-0000-0000-test00000001",
          email: "tinker-test@example.com",
          firstName: "Tinker",
          lastName: "Test",
          passwordHash: "Password123!",
          createdAt: "2026-01-01T00:00:00.000Z",
        },
      ]
    : [];

let configuredDefaultUsers: StoredUser[] | null = null;

function toStoredSeedUser(user: MockAuthSeedUser): StoredUser {
  return {
    id: user.id,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phone: user.phone,
    passwordHash: user.password,
    createdAt: new Date().toISOString(),
  };
}

function getDefaultUsers(): StoredUser[] {
  const users = configuredDefaultUsers ?? DEFAULT_MOCK_USERS;
  return users.map((user) => ({ ...user }));
}

function getCustomStoredUsers(): StoredUser[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(MOCK_USERS_KEY) || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function getStoredUsers(): StoredUser[] {
  const byEmail = new Map<string, StoredUser>();
  [...getDefaultUsers(), ...getCustomStoredUsers()].forEach((user) => {
    byEmail.set(user.email.toLowerCase(), user);
  });
  return Array.from(byEmail.values());
}

function saveStoredUsers(users: StoredUser[]): void {
  const defaultEmails = new Set(
    getDefaultUsers().map((user) => user.email.toLowerCase()),
  );
  const customUsers = users.filter(
    (user) => !defaultEmails.has(user.email.toLowerCase()),
  );
  localStorage.setItem(MOCK_USERS_KEY, JSON.stringify(customUsers));
}

function toBase64Url(str: string): string {
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function makeFakeJwt(payload: Record<string, unknown>): string {
  const header = toBase64Url(JSON.stringify({ alg: "HS256", typ: "JWT" }));
  const body = toBase64Url(
    JSON.stringify({
      ...payload,
      jti: crypto.randomUUID(),
      iat: Math.floor(Date.now() / 1000),
      exp: Math.floor(Date.now() / 1000) + 3600,
    }),
  );
  const sig = toBase64Url("mock-signature");
  return `${header}.${body}.${sig}`;
}

function makeAuthToken(user: User): AuthToken {
  return {
    // RCA-fix (2026-06-19 v5): Include borrower_id explicitly so the backend
    // DevMockAuthMiddleware JWT-GUID preference code (which checks borrower_id before sub)
    // finds the exact GUID without any email→mapping fallback.
    accessToken: makeFakeJwt({
      sub: user.id,
      borrower_id: user.id,
      email: user.email,
      name: `${user.firstName} ${user.lastName}`,
    }),
    refreshToken: makeFakeJwt({ sub: user.id, type: "refresh" }),
    expiresIn: 3600,
    tokenType: "Bearer",
  };
}

function toUser(stored: StoredUser): User {
  return {
    id: stored.id,
    email: stored.email,
    firstName: stored.firstName,
    lastName: stored.lastName,
    phone: stored.phone,
    createdAt: stored.createdAt,
    updatedAt: stored.createdAt,
  };
}

/**
 * Normalize register() inputs so callers can pass either a RegisterRequest
 * object or legacy positional args:
 *   register(email, password, "First Last")
 *   register(email, password, firstName, lastName)
 * Existing tests rely on the positional form; the interface documents the
 * object form. Keep both working.
 */
function normalizeRegisterInput(
  input: RegisterRequest | string,
  passwordArg?: string,
  nameOrFirstNameArg?: string,
  lastNameArg?: string,
): RegisterRequest {
  if (typeof input !== "string") {
    return input;
  }
  const email = input;
  const password = passwordArg ?? "";
  const nameOrFirst = nameOrFirstNameArg ?? "";
  let firstName = "";
  let lastName = "";
  if (lastNameArg !== undefined) {
    firstName = nameOrFirst;
    lastName = lastNameArg;
  } else {
    const parts = nameOrFirst.trim().split(/\s+/).filter(Boolean);
    firstName = parts[0] ?? "";
    lastName = parts.slice(1).join(" ") || "User";
  }
  return { email, password, firstName, lastName };
}

export class MockAuthProvider implements AuthProvider {
  /** Stable flag — survives minification, unlike constructor.name */
  readonly isMock = true as const;

  private _persist = localStorage.getItem(MOCK_PERSIST_KEY) !== "0";
  // BUG-DEEP-004 fix: strict mode ON by default so only seeded test users can log in.
  // Root cause: _strictMode=false caused auto-registration of any unknown email,
  // making all credentials valid. Strict mode throws 'Invalid email or password'
  // for unrecognised emails and wrong passwords on known accounts.
  private _strictMode = true;

  /** Set whether tokens are persisted across tabs (localStorage) or session-only (sessionStorage). */
  setPersist(persist: boolean): void {
    this._persist = persist;
    if (persist) {
      localStorage.removeItem(MOCK_PERSIST_KEY);
    } else {
      localStorage.setItem(MOCK_PERSIST_KEY, "0");
    }
  }

  setDefaultUsers(users: MockAuthSeedUser[]): void {
    configuredDefaultUsers = users.map(toStoredSeedUser);
  }

  /** Set strict mode - when enabled, invalid credentials throw errors instead of auto-registering */
  setStrictMode(strict: boolean): void {
    this._strictMode = strict;
  }

  private get _storage(): Storage {
    return this._persist ? localStorage : sessionStorage;
  }

  private _saveSession(token: AuthToken, user: User): void {
    const s = this._storage;
    s.setItem(MOCK_TOKEN_KEY, token.accessToken);
    s.setItem(MOCK_REFRESH_KEY, token.refreshToken);
    s.setItem(MOCK_USER_KEY, JSON.stringify(user));
  }

  private _clearSession(): void {
    for (const s of [localStorage, sessionStorage]) {
      s.removeItem(MOCK_TOKEN_KEY);
      s.removeItem(MOCK_REFRESH_KEY);
      s.removeItem(MOCK_USER_KEY);
      // Sweep the retired NP-59 flag so stale values from older builds
      // never linger in either storage.
      s.removeItem(LEGACY_AUTH_VIA_REAL_API_KEY);
    }
    // Keep MOCK_PERSIST_KEY to preserve user's persistence preference

    // Clear session-scoped path_/path- localStorage keys to prevent stale state
    // after logout (e.g. a submitted status persisting into the next session).
    // NOTE: the filter below intentionally PRESERVES several keys — do NOT
    // "clean up" the exception list:
    //   • path_persist_session    — user's persistence preference
    //   • path_mock_consent_state — e-consent across logouts
    //   • path-theme              — NP-45 dark/light choice across logout/login
    //   • path_mock_users (R31)   — registered accounts; wiping them broke re-login
    if (typeof window !== "undefined" && window.localStorage) {
      Object.keys(window.localStorage)
        .filter(
          (k) =>
            (k.startsWith("path_") || k.startsWith("path-")) &&
            k !== "path_persist_session" &&
            k !== "path_mock_consent_state" && // preserve consent across logouts
            // The Sophia AI consent record is an audit trail of when this
            // borrower agreed, not session state — sweeping it would erase the
            // history. The gate itself is per page load and holds nothing here.
            k !== "path_sophia_consent" &&
            k !== "path-theme" && // NP-45: preserve theme choice across logout/login
            k !== "path_lo_email" && // NP-415: LO assignment persists across sessions
            // NP-505: which branch each loan belongs to is not session state, and
            // re-fetching it after every login is the delay QA reported. Same
            // reasoning as path_lo_email above; it is keyed by loan GUID, so another
            // borrower on this browser can never match an entry.
            k !== LO_BY_LOAN_STORAGE_KEY &&
            // NP-430: the borrower-scoped draft stash is written moments before
            // this sweep (AuthContext.logout) so Income & Assets work entered
            // before Personal Info survives a re-login. Sweeping it defeats the
            // whole mechanism.
            !k.startsWith("path-application-draft:owner:") &&
            // NP-614: LoanContext's own comment on this key says it "deliberately
            // outlives a logout" — it is owner-stamped (see readPersistedLoanId),
            // so a different account can never inherit it, and NP-529's session-owner
            // watch re-reads it for whoever signs in next. This blanket sweep swept
            // it anyway, so any logout (including the 30-minute inactivity timeout in
            // SessionTimeout) severed a borrower from an already-created, loan-numbered
            // application that only sat idle — leaving the fragile /loans/incomplete
            // resume probe as the sole recovery, which can fail "degraded" at submit
            // time with no retry.
            k !== "path-active-loan-id" &&
            k !== MOCK_USERS_KEY,
        ) // R31: preserve registered user accounts across logouts
        .forEach((k) => window.localStorage.removeItem(k));
    }
  }

  /** Read a value from whichever storage has it */
  private _read(key: string): string | null {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  }

  async login(
    email: string,
    password: string,
    inviteToken?: string,
    otc?: string,
  ): Promise<LoginResult> {
    // R33a: Try real API first; fall back to local mock users on network error.
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
    if (apiBaseUrl) {
      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(loginBody(email, password, inviteToken, otc)),
        });

        console.log("[MockAuth] real API login response:", { res });
        if (res.ok) {
          const data = await res.json();
          const user: User = {
            id: data.borrower?.borrowerId ?? data.borrower?.id ?? "",
            email: data.borrower?.email ?? email,
            firstName: data.borrower?.firstName ?? "",
            lastName: data.borrower?.lastName ?? "",
            phone: data.borrower?.phone,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          const token: AuthToken = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expiresIn: data.expiresIn ?? 3600,
            tokenType: "Bearer",
          };
          this._saveSession(token, user);
          console.log("[MockAuth] real API login success:", {
            userId: user.id,
            email: user.email,
          });
          return {
            user,
            token,
            linkedLoanId: linkedLoanIdFrom(data),
            loanOfficerEmail: loanOfficerEmailFrom(data),
          };
        }
        // Surfaced error messages from API (401 account locked, etc.)
        let errMsg = "Invalid email or password";
        try {
          const errBody = await res.json();
          errMsg = errBody.message || errBody.error || errMsg;
        } catch {
          /* ignore */
        }
        // 409 conflict = account exists (should not occur on login but guard anyway)
        throw new Error(errMsg);
      } catch (err) {
        if (err instanceof TypeError) {
          // Network error — fall through to mock fallback
          console.warn(
            "[MockAuth] Real API unreachable, falling back to mock users:",
            err.message,
          );
        } else {
          // API returned a real error (bad credentials, locked, etc.) — surface it
          throw err;
        }
      }
    }

    // --- Local mock fallback (offline/demo mode) ---
    await new Promise((r) => setTimeout(r, 300 + Math.random() * 400));
    // R32c: normalize email to lowercase so login always matches regardless of
    // how the user types their email (e.g. 'Trae.y@test.com' vs 'trae.y@test.com').
    const normalizedEmail = email.toLowerCase();
    const users = getStoredUsers();
    console.log("[MockAuth] login attempt:", {
      email: normalizedEmail,
      userCount: users.length,
      userEmails: users.map((u) => u.email),
    });
    let found = users.find((u) => u.email.toLowerCase() === normalizedEmail);
    console.log("[MockAuth] user lookup:", {
      found: !!found,
      email: normalizedEmail,
    });

    // In strict mode, return error for unknown email
    if (!found) {
      if (this._strictMode) {
        throw new Error("Invalid email or password");
      }

      // Non-strict mode: auto-register if user doesn't exist (handles cleared localStorage)
      console.log("[MockAuth] auto-registering new user:", normalizedEmail);
      const [firstName, ...rest] = normalizedEmail.split("@")[0].split(".");
      const lastName = rest.length > 0 ? rest.join(" ") : "User";
      const stored: StoredUser = {
        id: crypto.randomUUID(),
        email: normalizedEmail,
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1),
        passwordHash: password,
        createdAt: new Date().toISOString(),
      };
      saveStoredUsers([...users, stored]);
      found = stored;
    }

    // Check password based on mode
    const isDefaultUser =
      DEFAULT_MOCK_USERS.some(
        (u) => u.email.toLowerCase() === normalizedEmail,
      ) ||
      (configuredDefaultUsers ?? []).some(
        (u) => u.email.toLowerCase() === normalizedEmail,
      );
    console.log("[MockAuth] password check:", {
      isDefault: isDefaultUser,
      passwordMatch: found.passwordHash === password,
      strict: this._strictMode,
    });

    if (found.passwordHash !== password) {
      if (this._strictMode) {
        // Strict mode: reject invalid passwords
        throw new Error("Invalid email or password");
      }

      // Non-strict mode: auto-accept and update password for non-default users
      if (!isDefaultUser) {
        found.passwordHash = password;
        saveStoredUsers(
          users.map((u) =>
            u.email.toLowerCase() === normalizedEmail ? found! : u,
          ),
        );
      }
    }

    const user = toUser(found);
    const token = makeAuthToken(user);
    this._saveSession(token, user);
    console.log("[MockAuth] login success:", {
      userId: user.id,
      email: user.email,
    });
    return { user, token };
  }

  async register(
    payloadOrEmail: RegisterRequest | string,
    passwordArg?: string,
    nameOrFirstNameArg?: string,
    lastNameArg?: string,
  ): Promise<{ user: User; token: AuthToken }> {
    // R33a: Try real API first; fall back to local mock registration on network error.
    // Accept both the RegisterRequest object form and legacy positional args
    // (email, password, "Full Name") | (email, password, firstName, lastName)
    // so existing tests keep working without modification.
    const payload = normalizeRegisterInput(
      payloadOrEmail,
      passwordArg,
      nameOrFirstNameArg,
      lastNameArg,
    );
    const { email, password, firstName, lastName, phone } = payload;
    if (!email || typeof email !== "string")
      throw new Error("Email is required");
    if (!password || typeof password !== "string")
      throw new Error("Password is required");
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
    if (apiBaseUrl) {
      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/auth/register`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email,
            password,
            firstName,
            lastName,
            phone,
            ...capturedLoanOfficerField(),
            ...invitedLeadField(),
          }),
        });
        if (res.ok || res.status === 201) {
          const data = await res.json();
          // const user: User = {
          //   id: data.borrowerId ?? "",
          //   email: email.toLowerCase(),
          //   firstName,
          //   lastName,
          //   createdAt: new Date().toISOString(),
          //   updatedAt: new Date().toISOString(),
          // };
          // const token: AuthToken = {
          //   accessToken: data.accessToken,
          //   refreshToken: data.refreshToken,
          //   expiresIn: data.expiresIn ?? 3600,
          //   tokenType: "Bearer",
          // };
          // R33a FIX: Call _saveSession() so the user is immediately logged in after
          // registration via the real backend API. The backend returns a valid JWT
          // (accessToken + refreshToken) — we must persist them so subsequent API
          // calls are authenticated. Previously skipped (_saveSession not called),
          // meaning the user registered but was not logged in, causing a redirect to
          // /login which then failed because the backend password was set but the
          // frontend mock localStorage had no record of the user.
          // this._saveSession(token, user);
          console.log("[MockAuth] real API register success:", data);
          return data;
        }
        const errBody = await res.json().catch(() => null);
        // A rejected Password (e.g. the server-side breach check) is shown as a
        // generic strong-password prompt rather than the raw backend text.
        if (fieldErrorsFromBody(errBody)?.Password?.length)
          throw new Error("Account creation failed. Please input a strong password.");
        // NP-649: before the duplicate check — a pre-created co-borrower row comes back
        // under the same errors.Email key, and only this one has a link to offer.
        if (isPendingActivationProblem(fieldErrorsFromBody(errBody)))
          throw new PendingActivationError(
            messageFromErrorBody(
              errBody,
              "Your account was created for you and still needs a password.",
            ),
          );
        // NP-447: duplicate email arrives as 400 + errors.Email. The legacy 409
        // is still honoured, but it is NOT the signal — the 400 body is.
        if (
          res.status === 409 ||
          isDuplicateEmailProblem(fieldErrorsFromBody(errBody))
        )
          throw new DuplicateEmailError(
            messageFromErrorBody(errBody, "An account with this email already exists."),
          );
        throw new Error(messageFromErrorBody(errBody, "Registration failed"));
      } catch (err) {
        if (err instanceof TypeError) {
          // Network error — fall through to mock fallback
          console.warn(
            "[MockAuth] Real API unreachable, falling back to mock registration:",
            err.message,
          );
        } else {
          throw err;
        }
      }
    }

    // --- Local mock fallback (offline/demo mode) ---
    await new Promise((r) => setTimeout(r, 400 + Math.random() * 300));
    const users = getStoredUsers();
    if (users.some((u) => u.email.toLowerCase() === email.toLowerCase())) {
      // NP-447: typed so the offline/dev path offers the same Sign In link as
      // the real API path. VITE_AUTH_PROVIDER defaults to mock in dev, so
      // without this the fix would be invisible to most local testing.
      throw new DuplicateEmailError("An account with this email already exists");
    }
    const stored: StoredUser = {
      id: crypto.randomUUID(),
      // R32c: normalize email to lowercase on registration so login lookups
      // are always case-insensitive — prevents 'Trae.y@test.com' vs 'trae.y@test.com'
      // mismatch that caused registered users to fail login after page reload.
      email: email.toLowerCase(),
      firstName,
      lastName,
      // NP-588: dropped here as well, so the offline/demo path reproduced the same
      // missing-phone gap as the real provider.
      phone,
      passwordHash: password,
      createdAt: new Date().toISOString(),
    };
    saveStoredUsers([...users, stored]);
    const user = toUser(stored);
    // R31 FIX: Return a token so the return type matches the AuthProvider interface.
    // The Register page does not auto-login (it redirects to /login with success state),
    // but the token is required for the type contract. We do NOT call _saveSession() here
    // because registration intentionally requires a separate login step.
    const token = makeAuthToken(user);
    return { user, token };
  }

  async logout(): Promise<void> {
    this._clearSession();
  }

  async resetPassword(email: string): Promise<void> {
    // R33a: Try real API first; fall back to mock on network error.
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
    if (apiBaseUrl) {
      const lo = getStoredLoEmail();
      const payload = lo ? { email, loanOfficerEmail: lo } : { email };

      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/auth/reset-password`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        // 202 Accepted is the success response (prevents email enumeration)
        if (res.ok || res.status === 202) return;
        // Non-network errors: surface them
        let errMsg = "Password reset request failed";
        try {
          const errBody = await res.json();
          errMsg = errBody.message || errBody.error || errMsg;
        } catch {
          /* ignore */
        }
        throw new Error(errMsg);
      } catch (err) {
        if (err instanceof TypeError) {
          // Network error — fall through to mock
          console.warn(
            "[MockAuth] Real API unreachable, mock password reset for:",
            email,
          );
        } else {
          throw err;
        }
      }
    }
    await new Promise((r) => setTimeout(r, 500));
    // Mock: just succeed
    console.log(`[MockAuth] Password reset email would be sent to ${email}`);
  }

  async getToken(): Promise<string | null> {
    return this._read(MOCK_TOKEN_KEY);
  }

  async refreshToken(): Promise<AuthToken> {
    // R33a: Try real API refresh first; fall back to mock token generation.
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || "";
    const storedRefresh = this._read(MOCK_REFRESH_KEY);
    if (apiBaseUrl && storedRefresh) {
      try {
        const res = await fetch(`${apiBaseUrl}/api/v1/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refreshToken: storedRefresh }),
        });
        if (res.ok) {
          const data = await res.json();
          const token: AuthToken = {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken,
            expiresIn: data.expiresIn ?? 3600,
            tokenType: "Bearer",
          };
          const userJson = this._read(MOCK_USER_KEY);
          if (userJson) {
            const user: User = JSON.parse(userJson);
            this._saveSession(token, user);
          }
          return token;
        }
      } catch (err) {
        if (err instanceof TypeError) {
          console.warn(
            "[MockAuth] Real API refresh unreachable, falling back to mock token",
          );
        } else {
          throw err;
        }
      }
    }
    // --- Mock fallback ---
    const userJson = this._read(MOCK_USER_KEY);
    if (!userJson) throw new Error("No active session");
    const user: User = JSON.parse(userJson);
    const token = makeAuthToken(user);
    this._saveSession(token, user);
    return token;
  }

  async updateStoredUser(patch: Partial<User>): Promise<User | null> {
    // NP-497 — written back to whichever storage HOLDS the session, so the merge
    // survives a refresh exactly as the login-time write does. Updating only React
    // state would make the fix evaporate on reload.
    //
    // Review catch: this originally read through `_read` (which prefers localStorage)
    // but wrote through `this._storage` (which follows the persist flag). When both
    // storages held a copy — or the flag disagreed with reality — it merged one copy
    // and wrote the result into the other, so the update could silently land on the
    // wrong record or fork into two. Resolving the storage ONCE and using it for both
    // sides removes the possibility rather than documenting it.
    const store = [localStorage, sessionStorage].find((s) => s.getItem(MOCK_USER_KEY) !== null);
    if (!store) return null;

    const json = store.getItem(MOCK_USER_KEY);
    if (!json) return null;
    try {
      const merged = { ...(JSON.parse(json) as User), ...patch };
      store.setItem(MOCK_USER_KEY, JSON.stringify(merged));
      return merged;
    } catch {
      // A corrupt stored user is not worth failing a profile save over — the caller
      // still has the server's response and React state is updated regardless.
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const token = this._read(MOCK_TOKEN_KEY);
    const json = this._read(MOCK_USER_KEY);
    if (!token || !json) return null;
    try {
      return JSON.parse(json) as User;
    } catch {
      this._clearSession();
      return null;
    }
  }
}

// ---------- Real Auth Provider (PATH.Api first-party JWT) ----------

const REAL_TOKEN_KEY = MOCK_TOKEN_KEY;
const REAL_REFRESH_KEY = MOCK_REFRESH_KEY;
const REAL_USER_KEY = MOCK_USER_KEY;
const REAL_PERSIST_KEY = MOCK_PERSIST_KEY;

/**
 * Build a human-readable message from an already-parsed error body.
 * ASP.NET ProblemDetails validation responses (400) carry the actionable reason
 * inside `errors: { Field: ["message", ...] }` and only a generic
 * "Validation failed" in `title`. Surface the field-level messages so users see
 * e.g. "This password has appeared in a known data breach." or
 * "An account with this email already exists." instead of "Validation failed".
 */
function messageFromErrorBody(body: unknown, fallback: string): string {
  const b = body as
    | {
        errors?: Record<string, unknown>;
        message?: string;
        error?: string;
        detail?: string;
        title?: string;
      }
    | null
    | undefined;
  const fieldErrors = b?.errors;
  if (fieldErrors && typeof fieldErrors === "object") {
    const messages = Object.values(fieldErrors)
      .flat()
      .filter((m): m is string => typeof m === "string" && m.trim().length > 0);
    if (messages.length > 0) return messages.join(" ");
  }
  // `detail` before `title`: ProblemDetails `title` is the generic category, while
  // `detail` carries the specific reason for non-validation 4xx/5xx responses
  // (consistent with extractBodyMessage in src/lib/errors/userMessages.ts).
  return b?.message || b?.error || b?.detail || b?.title || fallback;
}

/** Read the ProblemDetails `errors` map from an already-parsed body (or null). */
function fieldErrorsFromBody(body: unknown): Record<string, string[]> | null {
  const errors = (body as { errors?: unknown } | null | undefined)?.errors;
  return errors && typeof errors === "object"
    ? (errors as Record<string, string[]>)
    : null;
}

async function parseErrorMessage(
  res: Response,
  fallback: string,
): Promise<string> {
  try {
    return messageFromErrorBody(await res.json(), fallback);
  } catch {
    return fallback;
  }
}

export class RealAuthProvider implements AuthProvider {
  private baseUrl: string;
  private _persist = localStorage.getItem(REAL_PERSIST_KEY) !== "0";

  constructor() {
    this.baseUrl = import.meta.env.VITE_API_BASE_URL || "";
    if (!this.baseUrl) {
      console.warn(
        "[RealAuth] VITE_API_BASE_URL is not set — auth requests will fail",
      );
    }
  }

  setPersist(persist: boolean): void {
    this._persist = persist;
    if (persist) {
      localStorage.removeItem(REAL_PERSIST_KEY);
    } else {
      localStorage.setItem(REAL_PERSIST_KEY, "0");
    }
  }

  private get _storage(): Storage {
    return this._persist ? localStorage : sessionStorage;
  }

  private _read(key: string): string | null {
    return localStorage.getItem(key) ?? sessionStorage.getItem(key);
  }

  private _saveSession(token: AuthToken, user: User): void {
    const s = this._storage;
    s.setItem(REAL_TOKEN_KEY, token.accessToken);
    s.setItem(REAL_REFRESH_KEY, token.refreshToken);
    s.setItem(REAL_USER_KEY, JSON.stringify(user));
  }

  private _clearSession(): void {
    for (const s of [localStorage, sessionStorage]) {
      s.removeItem(REAL_TOKEN_KEY);
      s.removeItem(REAL_REFRESH_KEY);
      s.removeItem(REAL_USER_KEY);
    }
    // Clear session-scoped path_/path- keys but PRESERVE the exceptions below
    // (persistence pref, e-consent, NP-45 theme choice, registered accounts) —
    // do NOT "clean up" this exception list.
    if (typeof window !== "undefined" && window.localStorage) {
      Object.keys(window.localStorage)
        .filter(
          (k) =>
            (k.startsWith("path_") || k.startsWith("path-")) &&
            k !== REAL_PERSIST_KEY &&
            k !== "path_mock_consent_state" &&
            k !== "path_sophia_consent" && // audit trail, not session state
            k !== "path-theme" && // NP-45: preserve theme choice across logout/login
            k !== "path_lo_email" && // NP-415: LO assignment persists across sessions
            // NP-505: which branch each loan belongs to is not session state, and
            // re-fetching it after every login is the delay QA reported. Same
            // reasoning as path_lo_email above; it is keyed by loan GUID, so another
            // borrower on this browser can never match an entry.
            k !== LO_BY_LOAN_STORAGE_KEY &&
            // NP-430: the borrower-scoped draft stash is written moments before
            // this sweep (AuthContext.logout) so Income & Assets work entered
            // before Personal Info survives a re-login. Sweeping it defeats the
            // whole mechanism.
            !k.startsWith("path-application-draft:owner:") &&
            // NP-614: LoanContext's own comment on this key says it "deliberately
            // outlives a logout" — it is owner-stamped (see readPersistedLoanId),
            // so a different account can never inherit it, and NP-529's session-owner
            // watch re-reads it for whoever signs in next. This blanket sweep swept
            // it anyway, so any logout (including the 30-minute inactivity timeout in
            // SessionTimeout) severed a borrower from an already-created, loan-numbered
            // application that only sat idle — leaving the fragile /loans/incomplete
            // resume probe as the sole recovery, which can fail "degraded" at submit
            // time with no retry.
            k !== "path-active-loan-id" &&
            k !== MOCK_USERS_KEY,
        )
        .forEach((k) => window.localStorage.removeItem(k));
    }
  }

  async login(
    email: string,
    password: string,
    inviteToken?: string,
    otc?: string,
  ): Promise<LoginResult> {
    const res = await fetch(`${this.baseUrl}/api/v1/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginBody(email, password, inviteToken, otc)),
    });
    if (!res.ok) {
      throw new Error(
        await parseErrorMessage(res, "Invalid email or password"),
      );
    }
    const data = await res.json();
    const user: User = {
      id: data.borrower?.borrowerId ?? "",
      email: data.borrower?.email ?? email,
      firstName: data.borrower?.firstName ?? "",
      lastName: data.borrower?.lastName ?? "",
      phone: data.borrower?.phone ?? undefined,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    const token: AuthToken = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: Number(data.expiresIn) || 3600,
      tokenType: "Bearer",
    };
    this._saveSession(token, user);
    return {
      user,
      token,
      linkedLoanId: linkedLoanIdFrom(data),
      loanOfficerEmail: loanOfficerEmailFrom(data),
    };
  }

  async register(payload: RegisterRequest) {
    // NP-588: `phone` was hardcoded null here while the Register page collected it
    // (NP-587) and RegisterRequest carried it, so a really-registered borrower had no
    // phone on their account. Nothing surfaced it — the four-field create precheck
    // simply skipped the loan create in silence.
    const { email, password, firstName, lastName, phone } = payload;
    const res = await fetch(`${this.baseUrl}/api/v1/auth/register`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        email,
        password,
        firstName: firstName || "",
        lastName,
        phone: phone || null,
        ...capturedLoanOfficerField(),
        ...invitedLeadField(),
      }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => null);
      // A rejected Password (e.g. the server-side breach check) is shown as a
      // generic strong-password prompt rather than the raw backend text.
      if (fieldErrorsFromBody(body)?.Password?.length) {
        throw new Error("Account creation failed. Please input a strong password.");
      }
      // NP-649: before the duplicate check — a pre-created co-borrower row comes back
      // under the same errors.Email key, and only this one has a link to offer.
      if (isPendingActivationProblem(fieldErrorsFromBody(body))) {
        throw new PendingActivationError(
          messageFromErrorBody(
            body,
            "Your account was created for you and still needs a password.",
          ),
        );
      }
      // NP-447: duplicate email arrives as 400 + errors.Email. The legacy 409
      // is still honoured, but it is NOT the signal — the 400 body is.
      if (res.status === 409 || isDuplicateEmailProblem(fieldErrorsFromBody(body))) {
        throw new DuplicateEmailError(
          messageFromErrorBody(body, "An account with this email already exists."),
        );
      }
      throw new Error(messageFromErrorBody(body, "Registration failed"));
    }
    const data = await res.json();
    // const user: User = {
    //   id: data.borrowerId ?? "",
    //   email: email.toLowerCase(),
    //   firstName: firstName || "",
    //   lastName,
    //   createdAt: new Date().toISOString(),
    //   updatedAt: new Date().toISOString(),
    // };
    // const token: AuthToken = {
    //   accessToken: data.accessToken,
    //   refreshToken: data.refreshToken,
    //   expiresIn: Number(data.expiresIn) || 3600,
    //   tokenType: "Bearer",
    // };
    // this._saveSession(token, user);
    return data;
  }

  async logout(): Promise<void> {
    this._clearSession();
  }

  async resetPassword(email: string): Promise<void> {
    const lo = getStoredLoEmail();
    const payload = lo ? { email, loanOfficerEmail: lo } : { email };

    const res = await fetch(`${this.baseUrl}/api/v1/auth/reset-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    // 202 Accepted is the success response (prevents email enumeration)
    if (!res.ok && res.status !== 202) {
      throw new Error(
        await parseErrorMessage(res, "Password reset request failed"),
      );
    }
  }

  async getToken(): Promise<string | null> {
    return this._read(REAL_TOKEN_KEY);
  }

  async refreshToken(): Promise<AuthToken> {
    const storedRefresh = this._read(REAL_REFRESH_KEY);
    if (!storedRefresh) throw new Error("No active session");
    const res = await fetch(`${this.baseUrl}/api/v1/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: storedRefresh }),
    });
    if (!res.ok) {
      throw new Error(await parseErrorMessage(res, "Token refresh failed"));
    }
    const data = await res.json();
    const token: AuthToken = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: Number(data.expiresIn) || 3600,
      tokenType: "Bearer",
    };
    const userJson = this._read(REAL_USER_KEY);
    if (userJson) {
      try {
        const user: User = JSON.parse(userJson);
        this._saveSession(token, user);
      } catch {
        // Ignore parse errors; token still returned
      }
    }
    return token;
  }

  async updateStoredUser(patch: Partial<User>): Promise<User | null> {
    // NP-497 — written back to whichever storage HOLDS the session, so the merge
    // survives a refresh exactly as the login-time write does. Updating only React
    // state would make the fix evaporate on reload.
    //
    // Review catch: this originally read through `_read` (which prefers localStorage)
    // but wrote through `this._storage` (which follows the persist flag). When both
    // storages held a copy — or the flag disagreed with reality — it merged one copy
    // and wrote the result into the other, so the update could silently land on the
    // wrong record or fork into two. Resolving the storage ONCE and using it for both
    // sides removes the possibility rather than documenting it.
    const store = [localStorage, sessionStorage].find((s) => s.getItem(REAL_USER_KEY) !== null);
    if (!store) return null;

    const json = store.getItem(REAL_USER_KEY);
    if (!json) return null;
    try {
      const merged = { ...(JSON.parse(json) as User), ...patch };
      store.setItem(REAL_USER_KEY, JSON.stringify(merged));
      return merged;
    } catch {
      // A corrupt stored user is not worth failing a profile save over — the caller
      // still has the server's response and React state is updated regardless.
      return null;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const token = this._read(REAL_TOKEN_KEY);
    const json = this._read(REAL_USER_KEY);
    if (!token || !json) return null;
    try {
      return JSON.parse(json) as User;
    } catch {
      this._clearSession();
      return null;
    }
  }
}

// ---------- Provider Factory ----------

const AUTH_PROVIDER_ENV = import.meta.env.VITE_AUTH_PROVIDER || "mock";

if (!import.meta.env.VITE_AUTH_PROVIDER && !import.meta.env.DEV) {
  console.warn(
    "[Auth] VITE_AUTH_PROVIDER not set — defaulting to mock. " +
      "Set VITE_AUTH_PROVIDER=real for production deployment.",
  );
}

export function createAuthProvider(): AuthProvider {
  switch (AUTH_PROVIDER_ENV) {
    case "real":
      return new RealAuthProvider();
    case "mock":
      return new MockAuthProvider();
    default:
      throw new Error(
        `Invalid or missing auth provider: '${AUTH_PROVIDER_ENV}'. ` +
          'Please set VITE_AUTH_PROVIDER to "mock" (development only) or "real" (production).',
      );
  }
}

export const authProvider = createAuthProvider();
